import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { ExportOutlined, ReloadOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { Modal, Button, Divider, Card, Tabs } from 'antd';
import { checkPermission } from '@/utils/permission';
import { CustomerCardStatus, CustomerServiceStatus } from '@/utils/helpers';
import { exportExcel, getListCustomer, removeCustomer } from '@/redux/actions/customerActions';
import BaseRangeFilter from '@/components/BaseRangeFilter';
import DynamicTable from '@/components/DynamicTable';
import useUrlFilters from '@/hooks/useUrlFilters';
import FilterableMultiSelect from '@/components/FilterableMultiSelect';
import FilterableMultiTreeSelect from '@/components/FilterableMultiTreeSelect';
import { buildCustomerColumns } from '@/columns/buildCustomerColumns';
import useExcelExportLite from '@/hooks/useExcelExportLite';
import CustomerScheduleDetail from './CustomerScheduleDetail';
import { fetchFilters } from '@/redux/actions/filterActions';
import CustomerAddForm from './CustomerAddForm';
import TelesaleFollowUpAssignForm from '@/components/TelesaleFollowUpAssignForm';
import BaseNumberRangeFilter from '@/components/BaseNumberRangeFilter';
import BaseSelect from '@/components/Elements/BaseSelect';

const { TabPane } = Tabs;

const title = 'Khách hàng';
const path = 'customers';
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: `/${path}`, breadcrumbName: title },
];


export default function Index() {

    const location = useLocation();
    const dispatch = useDispatch();

    const items = useSelector(state => state.customerReducer.list.items);
    const pagination = useSelector(state => state.customerReducer.list.pagination);

    const users = useSelector(state => state.config.users);
    const doctors = useSelector(state => state.config.doctors);
    const consultants = useSelector(state => state.config.consultants);

    const filterData = useSelector(state => state.filterReducer.list);

    const list_provinces = filterData?.provinces;
    const customer_source_active_list = filterData?.sources;
    const branchActiveList = filterData?.branches;
    const customerClassActiveList = filterData?.classes;
    const customer_group_list = filterData?.groups;
    const customerInteractiveTypeActiveList = filterData?.interactive_types;
    const customerInteractiveStatusActiveList = filterData?.interactive_statuses;
    const customerStatusActiveList = filterData?.statuses;
    const service_active_list = filterData?.services;

    useEffect(() => {
        dispatch(fetchFilters());
    }, [dispatch]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenScheduleDetail, setIsOpenScheduleDetail] = useState(false);
    const [currentData, setCurrentData] = useState(null);
    const [isTelesaleAssignOpen, setIsTelesaleAssignOpen] = useState(false);
    const [isOpenAddForm, setIsOpenAddForm] = useState(false);

    const routeName = 'customer';
    const allowCreate = checkPermission(routeName, 'create');
    const allowDelete = checkPermission(routeName, 'delete');
    const allowedExport = checkPermission(routeName, 'export');
    const allowAssign = checkPermission('telesale_follow_up', 'assign');

    const query = useMemo(() => {
        return qs.parse(location.search.slice(1));
    }, [location.search]);

    const loadData = useCallback((q = query) => {
        setIsLoading(true);
        dispatch(getListCustomer(q)).finally(() => setIsLoading(false));
    }, [dispatch]);

    useEffect(() => {
        loadData(query);
    }, [query, loadData]);

    const handleRemove = () => {
        dispatch(removeCustomer(selectedRowKeys)).then(() => {
            setSelectedRowKeys([]);
            loadData(query);
            setTimeout(() => Modal.destroyAll(), 800);
        });
    };

    const { handleFilterChange, handleFilterChanges } = useUrlFilters({
        initialExtra: { page: 1, per_page: 30 }, // tuỳ chọn
        rangeMap: {
            // Ngày tạo
            created_date: { from: 'created_date_from', to: 'created_date_to', type: 'date' },
            // Lịch hẹn
            appointment_schedule: { from: 'appointment_schedule_from', to: 'appointment_schedule_to', type: 'date' },
            // Ngày chốt
            usage_date: { from: 'usage_date_from', to: 'usage_date_to', type: 'date' },
            // Ngày sinh
            birthday: { from: 'birthday_from', to: 'birthday_to', type: 'month-day' },
            // Ngày cskh
            care_date: { from: 'care_date_from', to: 'care_date_to', type: 'date' },

            // // // Doanh số
            // revenue: { from: 'revenue_from', to: 'revenue_to', type: 'number' },

            // // Đã thanh toán
            // paid: { from: 'paid_from', to: 'paid_to', type: 'number' },
            // // Công nợ
            // debt: { from: 'debt_from', to: 'debt_to', type: 'number' },
            // // Tỷ lệ thu
            // collect_rate: { from: 'collect_rate_from', to: 'collect_rate_to', type: 'number' },
        }
    });

    const [exporting, queueExcelExport] = useExcelExportLite(exportExcel);

    const openScheduleDetail = (record) => {
        setIsOpenScheduleDetail(true);
        setCurrentData(record);
    }

    const onCancel = () => {
        setIsTelesaleAssignOpen(false);
        setSelectedRowKeys([]);
    }

    const openAddForm = (isOpenAddForm) => {
        setIsOpenAddForm(isOpenAddForm)
    }

    const columns = useMemo(() => buildCustomerColumns(
        {
            onOpenScheduleDetail: (record) => openScheduleDetail(record),
            loadData: loadData,
        },
        {
            // Ghi đè thuộc tính cột:
            overrides: {
                full_name: { width: 300 },
            },
        }
    ), [openScheduleDetail]);

    const REVENUE_PRESETS = useMemo(() => ([
        { value: '0_10m', label: '0 - 10 triệu' },
        { value: '10_30m', label: '10 - 30 triệu' },
        { value: '30_50m', label: '30 - 50 triệu' },
        { value: 'gt_50m', label: '≥ 50 triệu' },
        { value: 'custom', label: 'Tùy chỉnh' },
    ]), []);

    const isRevenueCustom = query?.revenue_range === 'custom';
    const isPaidCustom = query?.paid_range === 'custom';
    const isDebtCustom = query?.debt_range === 'custom';
    const isCollectRateCustom = query?.collect_rate_range === 'custom';

    const handleRevenuePresetChange = useCallback((val) => {
        if (!val) {
            handleFilterChanges({
                revenue_range: undefined,
                revenue_from: undefined,
                revenue_to: undefined,
                page: 1,
            });
            return;
        }

        if (val === 'custom') {
            handleFilterChanges({
                revenue_range: 'custom',
                // không xoá from/to ở đây, vì user có thể đang nhập
                page: 1,
            });
            return;
        }

        // preset => xoá from/to
        handleFilterChanges({
            revenue_range: val,
            revenue_from: undefined,
            revenue_to: undefined,
            page: 1,
        });
    }, [handleFilterChanges]);

    const handlePaidPresetChange = useCallback((val) => {
        if (!val) {
            handleFilterChanges({
                paid_range: undefined,
                paid_from: undefined,
                paid_to: undefined,
                page: 1,
            });
            return;
        }

        if (val === 'custom') {
            handleFilterChanges({
                paid_range: 'custom',
                // không xoá from/to ở đây, vì user có thể đang nhập
                page: 1,
            });
            return;
        }

        // preset => xoá from/to
        handleFilterChanges({
            paid_range: val,
            paid_from: undefined,
            paid_to: undefined,
            page: 1,
        });
    }, [handleFilterChanges]);

    const handleDebtPresetChange = useCallback((val) => {
        if (!val) {
            handleFilterChanges({
                debt_range: undefined,
                debt_from: undefined,
                debt_to: undefined,
                page: 1,
            });
            return;
        }

        if (val === 'custom') {
            handleFilterChanges({
                debt_range: 'custom',
                // không xoá from/to ở đây, vì user có thể đang nhập
                page: 1,
            });
            return;
        }

        // preset => xoá from/to
        handleFilterChanges({
            debt_range: val,
            debt_from: undefined,
            debt_to: undefined,
            page: 1,
        });
    }, [handleFilterChanges]);

    const handleCollectRatePresetChange = useCallback((val) => {
        if (!val) {
            handleFilterChanges({
                collect_rate_range: undefined,
                collect_rate_from: undefined,
                collect_rate_to: undefined,
                page: 1,
            });
            return;
        }

        if (val === 'custom') {
            handleFilterChanges({
                collect_rate_range: 'custom',
                // không xoá from/to ở đây, vì user có thể đang nhập
                page: 1,
            });
            return;
        }

        // preset => xoá from/to
        handleFilterChanges({
            collect_rate_range: val,
            collect_rate_from: undefined,
            collect_rate_to: undefined,
            page: 1,
        });
    }, [handleFilterChanges]);

    useEffect(() => {
        if ((query.revenue_from || query.revenue_to) && query.revenue_range !== 'custom') {
            handleFilterChanges({ revenue_range: 'custom', page: 1 });
        }
    }, [query.revenue_from, query.revenue_to, query.revenue_range, handleFilterChanges]);

    useEffect(() => {
        if ((query.paid_from || query.paid_to) && query.paid_range !== 'custom') {
            handleFilterChanges({ paid_range: 'custom', page: 1 });
        }
    }, [query.paid_from, query.paid_to, query.paid_range, handleFilterChanges]);

    useEffect(() => {
        if ((query.debt_from || query.debt_to) && query.debt_range !== 'custom') {
            handleFilterChanges({ debt_range: 'custom', page: 1 });
        }
    }, [query.debt_from, query.debt_to, query.debt_range, handleFilterChanges]);

    useEffect(() => {
        if ((query.collect_rate_from || query.collect_rate_to) && query.collect_rate_range !== 'custom') {
            handleFilterChanges({ collect_rate_range: 'custom', page: 1 });
        }
    }, [query.collect_rate_from, query.collect_rate_to, query.collect_rate_range, handleFilterChanges]);

    const tables = <DynamicTable
        dataSource={items || []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        moduleKey="customer_list"
    />

    return (
        <div>
            <PageTitle routes={routes} title={title} />
            <TableActionBar
                isShowAddButton={allowCreate}
                isShowDeleteButton={allowDelete}
                onAdd={() => openAddForm(true)}
                disabled={!selectedRowKeys.length}
                onDelete={handleRemove}
                showSearch
                showFilter={true}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
                filterColSpan={6}
                activeFilter={
                    query.created_date_from ||
                    query.appointment_schedule_from ||
                    query.customer_class_id ||
                    query.customer_status_id ||
                    query.interested_service_id ||
                    query.selected_service_id ||
                    query.interaction_status_id ||
                    query.interaction_type_id ||
                    query.province_code ||
                    query.branch_id ||
                    query.customer_source_id ||
                    query.source_type ||
                    query.created_by ||
                    query.sales_staff ||
                    query.tele_staff ||
                    query.card_status ||
                    query.usage_date_from ||
                    query.group_ids ||
                    query.service_status ||
                    query.doctor_charge ||
                    query.consultant_staff ||
                    query.birthday_from ||
                    query.care_date_from ||
                    query.care_staff_id ||
                    query.revenue_range ||
                    query.revenue_from ||
                    query.revenue_to ||
                    query.paid_range ||
                    query.paid_from ||
                    query.paid_to ||
                    query.debt_range ||
                    query.debt_from ||
                    query.debt_to ||
                    query.collect_rate_range ||
                    query.collect_rate_from ||
                    query.collect_rate_to
                }
                filters={
                    [
                        <FilterableMultiSelect
                            options={branchActiveList || []}
                            placeholder="Chọn cơ sở"
                            parameterName='branch_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={customer_group_list || []}
                            placeholder="Chọn nhóm"
                            parameterName='group_ids'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <BaseRangeFilter
                            name="created_date"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Ngày tạo', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            name="appointment_schedule"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Lịch hẹn', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            name="usage_date"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Ngày chốt', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            type="month-day"
                            name="birthday"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Sinh nhật', 'Đến ngày']}
                        />,
                        <FilterableMultiTreeSelect
                            options={service_active_list || []}
                            placeholder="Dịch vụ quan tâm"
                            parameterName='interested_service_id'
                        />,
                        <FilterableMultiTreeSelect
                            options={service_active_list || []}
                            placeholder="Dịch vụ chốt"
                            parameterName='selected_service_id'
                        />,
                        <FilterableMultiSelect
                            options={CustomerServiceStatus || []}
                            placeholder="Trạng thái dịch vụ chốt"
                            parameterName='service_status'
                            optionValue="value"
                            optionLabel="label"
                        />,

                        <FilterableMultiSelect
                            options={customerClassActiveList || []}
                            placeholder="Hạng khách hàng"
                            parameterName='customer_class_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={customerStatusActiveList || []}
                            placeholder="Trạng thái khách hàng"
                            parameterName='customer_status_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={customerInteractiveTypeActiveList || []}
                            placeholder="Loại tương tác"
                            parameterName='interaction_type_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={customerInteractiveStatusActiveList || []}
                            placeholder="Trạng thái tương tác"
                            parameterName='interaction_status_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={list_provinces || []}
                            placeholder="Tỉnh/Thành phố"
                            parameterName='province_code'
                            optionValue="code"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={[
                                { value: 'from_message', label: 'Message' },
                                { value: 'from_manual', label: 'Manual' },
                                { value: 'from_crm', label: 'CRM' },
                                { value: 'from_potential', label: 'Potential' },
                                { value: 'import', label: 'Import' },
                                { value: 'import_receipt', label: 'Import receipt' },
                                { value: 'from_family', label: 'From family' }
                            ]}
                            placeholder="Nguồn tạo"
                            parameterName='source_type'
                            optionValue="value"
                            optionLabel="label"
                        />,
                        <FilterableMultiSelect
                            options={users || []}
                            placeholder="Người tạo"
                            parameterName='created_by'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <FilterableMultiSelect
                            options={users || []}
                            placeholder="Nhân viên telesale"
                            parameterName='tele_staff'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <FilterableMultiSelect
                            options={users || []}
                            placeholder="Nhân viên chốt"
                            parameterName='sales_staff'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <FilterableMultiSelect
                            options={customer_source_active_list || []}
                            placeholder="Nguồn khách hàng"
                            parameterName='customer_source_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={CustomerCardStatus || []}
                            placeholder="Trạng thái thẻ"
                            parameterName='card_status'
                            optionValue="value"
                            optionLabel="label"
                        />,
                        <FilterableMultiSelect
                            options={doctors || []}
                            placeholder="Bác sĩ thực hiện"
                            parameterName='doctor_charge'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <FilterableMultiSelect
                            options={consultants || []}
                            placeholder="Nhân viên tư vấn"
                            parameterName='consultant_staff'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <BaseRangeFilter
                            name="care_date"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Ngày CSKH', 'Đến ngày']}
                        />,
                        <FilterableMultiSelect
                            options={consultants || []}
                            placeholder="Nhân viên CSKH"
                            parameterName='care_staff_id'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <BaseSelect
                            options={REVENUE_PRESETS}
                            placeholder="Doanh số"
                            optionValue="value"
                            optionLabel="label"
                            className="w-100"
                            defaultValue={query.revenue_range || undefined}
                            allowClear
                            onChange={(val) => handleRevenuePresetChange(val)}
                        />,
                        isRevenueCustom ? (
                            <BaseNumberRangeFilter
                                name="revenue"
                                query={query}
                                onChangeFilter={handleFilterChange}
                                placeholder={['Từ (VNĐ)', 'Đến (VNĐ)']}
                                step={100000}
                            />
                        ) : null,
                        <BaseSelect
                            options={REVENUE_PRESETS}
                            placeholder="Đã thanh toán"
                            optionValue="value"
                            optionLabel="label"
                            className="w-100"
                            defaultValue={query.paid_range || undefined}
                            allowClear
                            onChange={(val) => handlePaidPresetChange(val)}
                        />,
                        isPaidCustom ? (
                            <BaseNumberRangeFilter
                                name="paid"
                                query={query}
                                onChangeFilter={handleFilterChange}
                                placeholder={['Từ (VNĐ)', 'Đến (VNĐ)']}
                                step={100000}
                            />
                        ) : null,
                        <BaseSelect
                            options={[
                                { label: '< 30%', value: 'lt_30' },
                                { label: '30 - 49%', value: '30_50' },
                                { label: '50 - 79%', value: '50_80' },
                                { label: '80 - 99%', value: '80_99' },
                                { label: '100%', value: 'eq_100' },
                                { value: 'custom', label: 'Tùy chỉnh' },
                            ]}
                            placeholder="Tỷ lệ thu"
                            optionValue="value"
                            optionLabel="label"
                            className="w-100"
                            defaultValue={query.collect_rate_range || undefined}
                            allowClear
                            onChange={(val) => handleCollectRatePresetChange(val)}
                        />,
                        isCollectRateCustom ? (
                            <BaseNumberRangeFilter
                                name="collect_rate"
                                query={query}
                                onChangeFilter={handleFilterChange}
                                placeholder={['Từ (%)', 'Đến (%)']}
                                step={5}
                            />
                        ) : null,
                        <BaseSelect
                            options={REVENUE_PRESETS}
                            placeholder="Công nợ"
                            optionValue="value"
                            optionLabel="label"
                            className="w-100"
                            defaultValue={query.debt_range || undefined}
                            allowClear
                            onChange={(val) => handleDebtPresetChange(val)}
                        />,
                        isDebtCustom ? (
                            <BaseNumberRangeFilter
                                name="debt"
                                query={query}
                                onChangeFilter={handleFilterChange}
                                placeholder={['Từ (VNĐ)', 'Đến (VNĐ)']}
                                step={100000}
                            />
                        ) : null
                    ]
                }
            >
                {
                    allowAssign ? (
                        <Button icon={<UserSwitchOutlined />} className="table-button" type='primary' disabled={!selectedRowKeys.length ? true : false} onClick={() => setIsTelesaleAssignOpen(true)}>Giao cho telesale</Button>
                    ) : null
                }
                {
                    allowedExport ? (
                        <Button className="table-button" loading={exporting} type='default' onClick={() => queueExcelExport()} icon={<ExportOutlined />}>Export</Button>
                    ) : null
                }
                <Button className='table-button' icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData(query)}>Refresh</Button>
                {selectedRowKeys.length > 0 && <span><Divider type="vertical" />Đang chọn {selectedRowKeys.length} bản ghi </span>}
            </TableActionBar>

            <Card size='small' bodyStyle={{ padding: 0 }} className='has-custom-column-table-btn'>
                <Tabs
                    onChange={(value) => handleFilterChange('tab', value)}
                    defaultActiveKey={query.tab}
                    type="card"
                    size="small"
                    tabBarStyle={{ fontWeight: 600 }}
                >
                    <TabPane tab="Toàn bộ khách hàng" key="all">
                        {tables}
                    </TabPane>
                    <TabPane tab="Khách còn công nợ < 50 triệu" key="debt_le_50">
                        {tables}
                    </TabPane>
                    <TabPane tab="Khách còn công nợ ≥ 50 triệu" key="debt_ge_50">
                        {tables}
                    </TabPane>
                    <TabPane tab="Khách gặp vấn đề" key="complaint">
                        {tables}
                    </TabPane>
                </Tabs>
            </Card>

            <CustomerAddForm
                visible={isOpenAddForm}
                onCancel={() => openAddForm(false)}
                reloadData={() => loadData()}
            />

            <CustomerScheduleDetail
                visible={isOpenScheduleDetail}
                currentItem={currentData}
                onCancel={() => setIsOpenScheduleDetail(false)}
            />

            <TelesaleFollowUpAssignForm
                visible={isTelesaleAssignOpen}
                customer_ids={selectedRowKeys}
                onCancel={() => onCancel()}
            />
        </div>
    );
}
